import { useCallback } from 'react';

import {
  buildActionBpContext,
  buildProductAnalyticsBpContext,
  buildTeamBpContextFromTeamUser,
  buildThirdPartyIntegrationBpContext,
  buildWebBpContext,
  buildViewBpContext,
  buildUpsellBpContext,
  sendBpEventWithContexts,
  UpsellBpContextFields,
} from '@vimeo/use-tracking';
import { useViewer } from '@vimeo/viewer';

import { TrackingProps } from '../interfaces';

export const useTracking = () => {
  const viewer = useViewer();

  const actionContext = useCallback(
    () => buildActionBpContext({ action_type: 'click', feature: null }),
    [],
  );

  const viewContext = useCallback(
    () =>
      buildViewBpContext({
        view_type: 'impression',
        feature: null,
      }),
    [],
  );

  const webContext = useCallback(
    () =>
      buildWebBpContext({
        page_name: 'other',
      }),
    [],
  );

  const productAnalyticsContext = useCallback(
    () =>
      buildProductAnalyticsBpContext({
        product: 'monetization',
        feature: 'not_applicable_pageview',
        location: 'not_applicable_pageview',
      }),
    [],
  );

  const teamContext = useCallback(
    () => buildTeamBpContextFromTeamUser(viewer?.teamUser),
    [viewer?.teamUser],
  );

  const thirdPartyIntegrationContext = useCallback(
    () =>
      buildThirdPartyIntegrationBpContext({
        is_integration: false,
        integration_name: null,
        integration_id: null,
        is_partner: null,
      }),
    [],
  );

  const upsellContext = useCallback(
    ({
      upsell_feature_category,
      upsell_specific_feature,
      upsell_badge_location,
    }: {
      upsell_feature_category: UpsellBpContextFields['upsell_feature_category'];
      upsell_specific_feature: UpsellBpContextFields['upsell_specific_feature'];
      upsell_badge_location: UpsellBpContextFields['upsell_badge_location'];
    }) =>
      buildUpsellBpContext({
        upsell_feature_category,
        upsell_specific_feature,
        upsell_badge_location,
      }),
    [],
  );

  const sendUpsellEvent = useCallback(
    ({
      eventName,
      isAction,
      upsell_feature_category,
      upsell_specific_feature,
      upsell_badge_location,
    }: {
      eventName: 'vimeo.upsell_trigger_impression' | 'vimeo.trigger_upsell';
      isAction: boolean;
      upsell_feature_category: TrackingProps['upsell_feature_category'];
      upsell_specific_feature: TrackingProps['upsell_specific_feature'];
      upsell_badge_location: TrackingProps['upsell_badge_location'];
    }) => {
      const contexts = {
        ...(isAction ? actionContext() : viewContext()),
        ...webContext(),
        ...teamContext(),
        ...productAnalyticsContext(),
        ...thirdPartyIntegrationContext(),
        ...upsellContext({
          upsell_feature_category,
          upsell_specific_feature,
          upsell_badge_location,
        }),
      };

      sendBpEventWithContexts(eventName, contexts);
    },
    [
      actionContext,
      productAnalyticsContext,
      webContext,
      teamContext,
      thirdPartyIntegrationContext,
      viewContext,
      upsellContext,
    ],
  );

  return {
    sendUpsellEvent,
  };
};
